import { Menu, Transition } from '@headlessui/react';
import { EllipsisVerticalIcon } from '@heroicons/react/24/outline';
import { Fragment } from 'react';

export interface ActionMenuItem {
  label: string;
  icon: React.ComponentType<{ className?: string }>;
  onClick: () => void;
  variant?: 'default' | 'danger';
  hidden?: boolean;
}

interface ActionsMenuProps {
  actions: ActionMenuItem[];
  buttonClassName?: string;
  menuClassName?: string;
}

export function ActionsMenu({
  actions,
  buttonClassName = 'rounded-md p-1.5 text-gray-400 transition-colors hover:bg-gray-100 hover:text-gray-600',
  menuClassName = 'absolute right-0 z-10 mt-1.5 w-40 origin-top-right rounded-xl bg-white shadow-md ring-1 ring-gray-200/60 focus:outline-none',
}: ActionsMenuProps) {
  const visibleActions = actions.filter((action) => !action.hidden);
  const safeActions = visibleActions.filter(
    (action) => action.variant !== 'danger',
  );
  const dangerActions = visibleActions.filter(
    (action) => action.variant === 'danger',
  );

  if (visibleActions.length === 0) {
    return null;
  }

  const renderMenuItem = (action: ActionMenuItem, index: number) => (
    <Menu.Item key={`${action.label}-${index}`}>
      {({ active }) => {
        const Icon = action.icon;
        const isDanger = action.variant === 'danger';

        return (
          <button
            type="button"
            onClick={action.onClick}
            className={`flex w-full items-center gap-2.5 px-3.5 py-2.5 text-sm text-gray-600 transition-colors duration-100 ${
              active
                ? isDanger
                  ? 'bg-red-50/70 text-red-600'
                  : 'bg-gray-50 text-gray-900'
                : ''
            }`}
          >
            <Icon
              className={`size-4 transition-colors duration-100 ${
                active && isDanger ? 'text-red-500' : 'text-gray-400'
              }`}
            />
            {action.label}
          </button>
        );
      }}
    </Menu.Item>
  );

  return (
    <Menu as="div" className="relative shrink-0">
      <Menu.Button className={buttonClassName}>
        <span className="sr-only">Open actions menu</span>
        <EllipsisVerticalIcon className="size-5" />
      </Menu.Button>
      <Transition
        as={Fragment}
        enter="transition ease-out duration-100"
        enterFrom="transform opacity-0 scale-95"
        enterTo="transform opacity-100 scale-100"
        leave="transition ease-in duration-75"
        leaveFrom="transform opacity-100 scale-100"
        leaveTo="transform opacity-0 scale-95"
      >
        <Menu.Items className={menuClassName}>
          {safeActions.length > 0 && (
            <div className="py-1.5">{safeActions.map(renderMenuItem)}</div>
          )}
          {dangerActions.length > 0 && (
            <>
              {safeActions.length > 0 && (
                <div className="mx-3 border-t border-gray-100" />
              )}
              <div className="py-1.5">{dangerActions.map(renderMenuItem)}</div>
            </>
          )}
        </Menu.Items>
      </Transition>
    </Menu>
  );
}
